// Save engine utilities for questionnaire answers

import { getCookieStorage, timezone } from '@/utils';

export interface SaveAnswerPayload {
  answer: string;
  nutrition_customer_onboarding: number;
  question: number;
  answer_unit: string | null;
  selected_option: number | null;
}

export interface SaveProgress {
  current: number;
  total: number;
  questionId?: number;
  error?: string;
}

// Convert question answers to API payload format
export const convertAnswersToPayload = (
  question: any,
  nutritionCustomerOnboardingId: number
): SaveAnswerPayload[] => {
  const answers = question.customer_answer || [];
  
  if (answers.length === 0) {
    return [];
  }

  return answers.map(answer => ({
    answer: answer.answer || '',
    nutrition_customer_onboarding: nutritionCustomerOnboardingId,
    question: question.id,
    answer_unit: answer.answer_unit || null,
    selected_option: answer.selected_option || null
  }));
};

// Save single question answers
export const saveQuestionAnswers = async (
  payloads: SaveAnswerPayload[],
  files?: File[]
): Promise<any> => {
  const accessToken = getCookieStorage('__authAccessToken');
  
  if (!accessToken) {
    throw new Error('No access token found');
  }

  const url = `${process.env.NEXT_PUBLIC_LOCAL_API_URL}api/v1/nutrition/customer-answer/`;
  
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${accessToken}`,
  };

  let body: any;
  
  if (files && files.length > 0) {
    // Handle multipart request for file uploads - match backend expectation
    const formData = new FormData();
    const payload = payloads[0]; // For file uploads, there's only one payload
    
    // Add individual form fields as expected by backend
    formData.append('answer', payload.answer);
    formData.append('nutrition_customer_onboarding', String(payload.nutrition_customer_onboarding));
    formData.append('question', String(payload.question));
    formData.append('selected_option', String(payload.selected_option));
    formData.append('answer_unit', payload.answer_unit || '');
    
    // Add multiple files with indexed field names
    files.forEach((file, index) => {
      formData.append(`medical_docs[${index}]document`, file);
      formData.append(`medical_docs[${index}]document_type`, 'MEDICAL_RECORDS');
      formData.append(`medical_docs[${index}]uploaded_by`, 'Customer');
    });
    
    body = formData;
  } else {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(payloads);
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

// Save all answers for current category sequentially
export const saveAllCategoryAnswers = async (
  questions: any[],
  nutritionCustomerOnboardingId: number,
  onProgress: (progress: SaveProgress) => void
): Promise<{ success: boolean; error?: string }> => {
  const questionsWithAnswers = questions.filter(q => 
    q.customer_answer && q.customer_answer.length > 0
  );

  if (questionsWithAnswers.length === 0) {
    return { success: true };
  }

  const total = questionsWithAnswers.length;
  let current = 0;

  try {
    for (const question of questionsWithAnswers) {
      onProgress({ current, total, questionId: question.id });

      const payloads = convertAnswersToPayload(question, nutritionCustomerOnboardingId);
      
      if (payloads.length > 0) {
        // Check if this question has file uploads
        const fileAnswers = question.customer_answer.filter(ans => ans.files && ans.files.length > 0);
        const files = fileAnswers.length > 0 ? fileAnswers[0].files : undefined;
        
        await saveQuestionAnswers(payloads, files);
      }

      current++;
      onProgress({ current, total, questionId: question.id });

      // Small delay between requests to avoid overwhelming the server
      if (current < total) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    return { success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to save answers';
    onProgress({ current, total, error: errorMessage });
    return { success: false, error: errorMessage };
  }
};